import React from "react";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { AppleIcon, GoogleIcon } from "../../assets/icons";
import DataUpLogo from "../../assets/images/DataUpLogoAuth.svg";
import { Button } from "../Button";
import { Input, SelectInput } from "../Input";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import { signup } from "../../features/store/authSlice";
import languages from "../../data/languages.json";
import { loginApple, loginGoogle } from "../../features/store/authSlice";
import {
  SignUpFormContainerProps,
  SignUpFormProps,
  PasswordStrengthIndicatorProps,
  SubmitButtonProps,
  SignUpFormData,
} from "./types";
import useClickOutside from "../../hooks/useClickOutside";
import { isInReactNativeWebView, trackEvent } from "../../lib/appsflyer";

const requestNativeGoogleLogin = () => {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ action: "google-login" }));
};

const requestNativeAppleLogin = () => {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ action: "apple-login" }));
};

let appleScriptPromise: Promise<void> | null = null;

const loadAppleSignInScript = () => {
  if (window.AppleID?.auth) return Promise.resolve();
  if (appleScriptPromise) return appleScriptPromise;

  appleScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });

  return appleScriptPromise;
};

const socialButtonClass =
  "p-2 border rounded-md flex items-center justify-center gap-2 min-w-[220px] bg-white text-gray-800 dark:bg-transparent dark:border-gray-700 dark:text-white";

const NativeGoogleLoginButton = () => (
  <button
    type="button"
    onClick={requestNativeGoogleLogin}
    className={socialButtonClass}
  >
    <GoogleIcon />
    <span>Continue with Google</span>
  </button>
);

const AppleLoginButton = ({ onClick }: { onClick: () => void }) => (
  <button type="button" onClick={onClick} className={socialButtonClass}>
    <AppleIcon />
    <span>Continue with Apple</span>
  </button>
);

export const SignUpFormContainer: React.FC<SignUpFormContainerProps> = ({
  onSignIn,
  onSubmit,
  error,
  closeSignUpModal, // <-- Ensure this is included
}) => {
  const clickRef = useClickOutside(closeSignUpModal);

  const dispatch = useAppDispatch();
  const handleGoogleLogin = async (credentialResponse: any) => {
    if (!credentialResponse?.credential) {
      toast.error("Google login failed - no credentials received");
      return;
    }
    try {
      await dispatch(loginGoogle(credentialResponse.credential)).unwrap();
      trackEvent("af_complete_registration", { method: "google" });
      toast.success("🎉 Logged in successfully!");
      closeSignUpModal();
    } catch (error: any) {
      toast.error(error.message || "Google login failed. Please try again.");
    }
  };
  const handleAppleLogin = async () => {
    if (isInReactNativeWebView()) {
      requestNativeAppleLogin();
      return;
    }

    const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    if (!appleClientId) {
      toast.error("Apple login is not configured.");
      return;
    }

    try {
      await loadAppleSignInScript();
      if (!window.AppleID?.auth) {
        toast.error("Apple login is unavailable.");
        return;
      }

      const state = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const nonce = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.AppleID.auth.init({
        clientId: appleClientId,
        scope: "name email",
        redirectURI:
          process.env.REACT_APP_APPLE_REDIRECT_URI ||
          `${window.location.origin}/apple-auth`,
        state,
        nonce,
        usePopup: true,
      });

      const result = await window.AppleID.auth.signIn();
      const token = result?.authorization?.id_token;

      if (!token) {
        toast.error("Apple login failed.");
        return;
      }

      await dispatch(
        loginApple({
          token,
          firstname: result?.user?.name?.firstName,
          lastname: result?.user?.name?.lastName,
        }),
      ).unwrap();
      trackEvent("af_complete_registration", { method: "apple" });
      toast.success("🎉 Logged in successfully!");
      closeSignUpModal();
    } catch (error: any) {
      toast.error(error.message || "Apple login failed. Please try again.");
    }
  };
  const SocialLoginButtons = () => (
    <div className="flex flex-col gap-3 items-center justify-center">
      {isInReactNativeWebView() ? (
        <NativeGoogleLoginButton />
      ) : (
        <GoogleLogin
          width="220"
          onSuccess={handleGoogleLogin}
          onError={() => toast.error("Google login failed.")}
        />
      )}
      <AppleLoginButton onClick={handleAppleLogin} />
    </div>
  );

  return (
    <div ref={clickRef}>
      <Logo />
      <Title />
      <div className="space-y-4">
        <SocialLoginButtons />
        <OrDivider />
        <SignUpForm onSubmit={onSubmit} closeSignUpModal={closeSignUpModal} />
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <FooterLinks onSignIn={onSignIn} />
      </div>
    </div>
  );
};

const Logo = () => (
  <div className="flex justify-center mb-4">
    <img
      src={DataUpLogo}
      alt="DatauplogoAuth"
      className="w-[135.55px] h-[32px]"
    />
  </div>
);

const Title = () => (
  <h2 className="text-xl font-bold text-center mb-6 dark:text-white">
    Create an account
  </h2>
);

const OrDivider = () => (
  <div className="flex items-center my-4">
    <div className="flex-1 border-t border-gray-300"></div>
    <div className="px-4 text-gray-500 text-sm dark:text-white">OR</div>
    <div className="flex-1 border-t border-gray-300"></div>
  </div>
);

const SignUpForm = ({ closeSignUpModal }: SignUpFormProps) => {
  const schema = yup
    .object({
      email: yup.string().required().email().label("Email"),
      username: yup.string().required().label("Username"),
      firstname: yup.string().required().label("Firstname"),
      lastname: yup.string().required().label("Lastname"),
      othername: yup.string().optional().label("Othername"),
      phone_number: yup.string().required().label("Phone Number"),
      language: yup.string().required().label("Language"),
      password: yup.string().required().label("Password"),
    })
    .required();

  const { register, handleSubmit, reset, watch } = useForm<SignUpFormData>({
    resolver: yupResolver(schema),
  });

  const dispatch = useAppDispatch();

  const { loading } = useAppSelector((state) => state.auth);

  const onFormSubmit = async (data: SignUpFormData) => {
    await dispatch(signup(data))
      .unwrap()
      .then(() => {
        trackEvent("af_complete_registration", { method: "email" });
        toast.success("🎉 Account created successfully!");
        reset();
        closeSignUpModal();
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          type="text"
          placeholder="First Name"
          register={register}
          name="firstname"
          required
          // error={errors.firstname?.message}
        />

        <Input
          type="text"
          placeholder="Last Name"
          register={register}
          name="lastname"
          required
          // error={errors.lastname?.message}
        />
      </div>

      <Input
        type="text"
        placeholder="Other Name"
        register={register}
        name="othername"
        // error={errors.othername?.message}
      />

      <Input
        type="text"
        placeholder="Username"
        register={register}
        name="username"
        required
        // error={errors.username?.message}
      />

      <Input
        type="email"
        placeholder="Email address"
        register={register}
        name="email"
        required
        // error={errors.email?.message}
      />

      <Input
        type="text"
        placeholder="Phone Number"
        register={register}
        name="phone_number"
        required
        // error={errors.phone_number?.message}
      />

      <SelectInput
        options={languages}
        register={register}
        name="language"
        placeholder="Language"
        required
        // error={errors.language?.message}
      />

      <Input
        type="password"
        placeholder="Password"
        isPassword
        register={register}
        name="password"
        required
        // error={errors.password?.message}
      />
      <PasswordStrengthIndicator password={watch("password")} />
      <TermsAndConditions />
      <SubmitButton loading={loading} />
    </form>
  );
};

const PasswordStrengthIndicator = ({
  password,
}: PasswordStrengthIndicatorProps) => {
  const getStrength = (pw: string) => {
    if (!pw) return 0;
    if (pw.length < 4) return 1;
    if (pw.length < 8) return 2;
    if (!/[A-Z]/.test(pw) || !/[0-9]/.test(pw)) return 3;
    return 4;
  };

  const strength = getStrength(password);
  const strengthText = ["", "Very Weak", "Weak", "Medium", "Strong"];

  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 dark:text-white">
        {password
          ? `Strength: ${strengthText[strength]}`
          : "Must be at least 8 characters"}
      </p>
      <div className="w-full h-1 flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`flex-1 ${
              i <= strength
                ? strength < 3
                  ? "bg-red-500"
                  : strength < 4
                    ? "bg-yellow-500"
                    : "bg-green-500"
                : "bg-gray-200"
            } ${i === 1 ? "rounded-l-full" : ""} ${
              i === 5 ? "rounded-r-full" : ""
            } ml-0.5`}
          />
        ))}
      </div>
    </div>
  );
};

const TermsAndConditions = () => {
  return (
    <div className="relative">
      <p className="text-xs text-gray-500 dark:text-white">
        By creating an account, you agree to the{" "}
        <span className=" text-[#039EF2]">Terms & Conditions</span>.
      </p>
    </div>
  );
};

const SubmitButton = ({ loading }: SubmitButtonProps) => (
  <Button
    type="submit"
    bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
    textColor="text-white"
    className="w-full h-10 flex items-center justify-center"
    borderRadius="rounded-lg"
    disabled={loading}
  >
    {loading ? (
      <div className="flex items-center">
        <svg
          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Processing...
      </div>
    ) : (
      "Complete Sign Up"
    )}
  </Button>
);

const FooterLinks = ({ onSignIn }: { onSignIn: () => void }) => (
  <p className="text-center dark:text-white text-xs">
    Already have an account?{" "}
    <span
      className="text-[#039EF2] font-bold cursor-pointer"
      onClick={onSignIn}
    >
      Sign In
    </span>
  </p>
);
